import { useEffect, useRef, useState } from 'react';
import { Volume2, VolumeX, Bell } from 'lucide-react';
import { distToLines } from '../lib/dxf';
import { hav } from '../lib/track';

import { beep, unlockAudio, audioReady } from '../lib/sound';

// live elevation profile (m a.s.l. over km)
export function LiveProfile({ pts }) {
  const a = [];
  let d = 0;
  pts.forEach((p, i) => { if (i && !p.brk) d += hav(pts[i - 1], p); if (p.alt != null) a.push([d, p.alt]); });
  if (a.length < 2) return null;
  const W = 300, H = 56, lo = Math.min(...a.map((x) => x[1])), hi = Math.max(...a.map((x) => x[1])), sp = Math.max(hi - lo, 4), dx = a[a.length - 1][0] || 1;
  const path = a.map(([x, y], i) => `${i ? 'L' : 'M'}${((x / dx) * W).toFixed(1)},${(H - 4 - ((y - lo) / sp) * (H - 10)).toFixed(1)}`).join(' ');
  return (
    <div className="trk-prof-live" data-testid="track-live-profile">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"><path d={`${path} L${W},${H} L0,${H} Z`} fill="var(--c)" opacity="0.18" /><path d={path} fill="none" stroke="var(--c)" strokeWidth="2" vectorEffect="non-scaling-stroke" /></svg>
      <small>{Math.round(hi)} m</small><small className="lo">{Math.round(lo)} m · {(dx / 1000).toFixed(2).replace('.', ',')} km</small>
    </div>
  );
}

// off-route warning (> 15 m from reference) with tone
export function OffRoute({ t, pos, lines }) {
  const [mute, setMute] = useState(() => localStorage.getItem('giss_offroute_mute') === '1');
  const prev = useRef(false);
  const d = pos && lines?.length ? distToLines([pos.lat, pos.lon], lines) : null;
  const off = d != null && d > 15;
  const [ready, setReady] = useState(audioReady);
  useEffect(() => { if (off !== prev.current && d != null) { if (!mute) beep(off ? 880 : 520, off ? 660 : 780); prev.current = off; } }, [off, d, mute]);
  useEffect(() => {
    if (!off || mute) return undefined;
    const id = setInterval(() => beep(880, 660), 10000);
    return () => clearInterval(id);
  }, [off, mute]);
  const test = () => { unlockAudio(); beep(880, 660); setTimeout(() => setReady(audioReady()), 300); };
  if (d == null) return null;
  return (
    <div className={`trk-dev ${off ? 'off' : 'ok'}`} data-testid="track-deviation">
      <span data-testid="track-deviation-text">{off ? `⚠️ ${t.devOff}` : `✅ ${t.devOn}`} · {Math.round(d)} m</span>
      {!mute && !ready && <button className="btn btn-sm btn-accent" data-testid="track-deviation-unlock" onClick={test}>{t.devSoundOn}</button>}
      {!mute && ready && <button className="icon-btn" data-testid="track-deviation-test" title={t.devSoundTest} onClick={test}><Bell size={14} /></button>}
      <button className="icon-btn" data-testid="track-deviation-mute" onClick={() => { if (mute) unlockAudio(); localStorage.setItem('giss_offroute_mute', mute ? '0' : '1'); setMute(!mute); }}>{mute ? <VolumeX size={14} /> : <Volume2 size={14} />}</button>
    </div>
  );
}
